/**
 * Shared user form rules (user story: Radiologist / Technician creation).
 * Pure validators return an error message or undefined when valid.
 */

import { isWellFormedEmailAddress } from './emailValidation';

/** Max age for DOB validation (configurable). */
export const USER_DOB_MAX_AGE = 75;

export const USER_MSG = {
  role: 'Please select a role.',
  employeeId: 'Employee ID must be alphanumeric and unique.',
  fullName: 'Only letters and spaces are allowed.',
  gender: 'Please select gender.',
  dob: 'Enter a valid date of birth (Age must be 18+).',
  email: 'Enter a valid and unique email address.',
  phone: 'Enter a valid 10-digit mobile number.',
  username: 'Username must be lowercase, unique, and without spaces.',
  password:
    'Password must be at least 8 characters with upper, lower, number, and special character.',
  passwordNoIdentity: 'Password must not contain your username or email.',
  status: 'Please select user status.',
  experienceYears: 'Experience must be a whole number (years).',
} as const;

const EMPLOYEE_ID_RE = /^[A-Za-z0-9]+$/;
const FULL_NAME_RE = /^[A-Za-z ]+$/;
const PHONE_IN_RE = /^[6-9][0-9]{9}$/;
const USERNAME_RE = /^[a-z0-9._]+$/;
const USER_PASSWORD_RE = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&]).{8,}$/;

function trim(s: unknown): string {
  return String(s ?? '').trim();
}

/** Parse DOB from yyyy-mm-dd or dd/mm/yyyy; returns local date at noon to avoid TZ drift. */
export function parseUserDobInput(raw: unknown): Date | null {
  const s = trim(raw);
  if (!s) {
    return null;
  }
  if (s.includes('-')) {
    const parts = s.split('-').map(p => p.trim());
    if (parts.length !== 3) {
      return null;
    }
    const y = parseInt(parts[0], 10);
    const m = parseInt(parts[1], 10);
    const d = parseInt(parts[2], 10);
    if (Number.isNaN(y) || Number.isNaN(m) || Number.isNaN(d)) {
      return null;
    }
    const dt = new Date(y, m - 1, d, 12, 0, 0, 0);
    return Number.isNaN(dt.getTime()) ? null : dt;
  }
  const parts = s.split('/').map(p => p.trim());
  if (parts.length !== 3) {
    return null;
  }
  const [dd, mm, yyyy] = parts;
  if (dd.length !== 2 || mm.length !== 2 || yyyy.length !== 4) {
    return null;
  }
  const day = parseInt(dd, 10);
  const month = parseInt(mm, 10);
  const year = parseInt(yyyy, 10);
  if (Number.isNaN(day) || Number.isNaN(month) || Number.isNaN(year)) {
    return null;
  }
  const dt = new Date(year, month - 1, day, 12, 0, 0, 0);
  if (dt.getFullYear() !== year || dt.getMonth() !== month - 1 || dt.getDate() !== day) {
    return null;
  }
  return dt;
}

function ageOnDate(dob: Date, ref: Date): number {
  let age = ref.getFullYear() - dob.getFullYear();
  const m = ref.getMonth() - dob.getMonth();
  if (m < 0 || (m === 0 && ref.getDate() < dob.getDate())) {
    age -= 1;
  }
  return age;
}

export function validateUserDobStory(raw: unknown): string | undefined {
  const dob = parseUserDobInput(raw);
  if (!dob) {
    return USER_MSG.dob;
  }
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const dobDay = new Date(dob.getFullYear(), dob.getMonth(), dob.getDate());
  if (dobDay >= today) {
    return USER_MSG.dob;
  }
  const age = ageOnDate(dobDay, today);
  if (age < 18 || age > USER_DOB_MAX_AGE) {
    return USER_MSG.dob;
  }
  return undefined;
}

export function validateEmployeeIdStory(raw: unknown): string | undefined {
  const s = trim(raw);
  if (!s) {
    return USER_MSG.employeeId;
  }
  if (s.length < 3 || s.length > 20 || !EMPLOYEE_ID_RE.test(s)) {
    return USER_MSG.employeeId;
  }
  return undefined;
}

export function validateFullNameStory(raw: unknown): string | undefined {
  const s = trim(raw);
  if (!s) {
    return USER_MSG.fullName;
  }
  if (s.length < 3 || s.length > 100 || !FULL_NAME_RE.test(s)) {
    return USER_MSG.fullName;
  }
  return undefined;
}

export function validateGenderStory(raw: unknown): string | undefined {
  const s = trim(raw);
  if (!s || !['Male', 'Female', 'Other'].includes(s)) {
    return USER_MSG.gender;
  }
  return undefined;
}

export function validateEmailStory(raw: unknown): string | undefined {
  const s = trim(raw);
  if (!s || !isWellFormedEmailAddress(s)) {
    return USER_MSG.email;
  }
  return undefined;
}

export function validatePhoneStoryDigits(digits: string): string | undefined {
  const d = (digits || '').replace(/\D/g, '').slice(-10);
  if (d.length !== 10 || !PHONE_IN_RE.test(d)) {
    return USER_MSG.phone;
  }
  return undefined;
}

export function validateUsernameStory(raw: unknown): string | undefined {
  const s = trim(raw);
  if (!s || s.length < 5 || s.length > 30 || !USERNAME_RE.test(s)) {
    return USER_MSG.username;
  }
  return undefined;
}

/** Optional “years of experience”: empty is valid; otherwise digits only, 0–99. */
export function validateOptionalExperienceYears(raw: unknown): string | undefined {
  const s = trim(raw);
  if (!s) {
    return undefined;
  }
  if (!/^\d+$/.test(s)) {
    return USER_MSG.experienceYears;
  }
  const n = parseInt(s, 10);
  if (n < 0 || n > 99) {
    return USER_MSG.experienceYears;
  }
  return undefined;
}

export function validateUserPasswordStory(
  password: unknown,
  username: unknown,
  email: unknown
): string | undefined {
  const p = String(password ?? '');
  if (!p || p === '••••••') {
    return USER_MSG.password;
  }
  if (!USER_PASSWORD_RE.test(p)) {
    return USER_MSG.password;
  }
  const u = trim(username).toLowerCase();
  const em = trim(email).toLowerCase();
  const pl = p.toLowerCase();
  if (u.length >= 2 && pl.includes(u)) {
    return USER_MSG.passwordNoIdentity;
  }
  if (em.length >= 3 && pl.includes(em)) {
    return USER_MSG.passwordNoIdentity;
  }
  const local = em.split('@')[0];
  if (local && local.length >= 2 && pl.includes(local)) {
    return USER_MSG.passwordNoIdentity;
  }
  return undefined;
}

export function validateStatusStoryForAddUser(raw: unknown): string | undefined {
  const s = trim(raw).toLowerCase();
  if (!s || !['active', 'inactive'].includes(s)) {
    return USER_MSG.status;
  }
  return undefined;
}

/** User dialog uses Active / Inactive (capitalized). */
export function validateStatusStoryDialog(raw: unknown): string | undefined {
  const s = trim(raw);
  if (!s || !['Active', 'Inactive', 'active', 'inactive'].includes(s)) {
    return USER_MSG.status;
  }
  return undefined;
}
